import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '../../services/translate';
import { Pagination } from '../../components/Pagination';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { UserParamList } from '../../typings/Params';
import MyDataTable from '../../components/MyDataTable';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/Auth';
import moment from 'moment';
import { centralAPI } from '../../services/central-api';
import DropdownButton from '../../components/DropdownButton';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import DataTableContainer from '../../components/DataTableContainer';
import CustomButton from '../../components/CustomButton';

export default function OwnSupportTickets() {
    const route = useRoute<RouteProp<UserParamList, 'OwnSupportTickets'>>();
    const navigation = useNavigation<NavigationProp<UserParamList>>();
    const { user: authenticatedUser } = useAuth();

    const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const ticketQuery = useQuery({
        queryKey: ['tickets', filters],
        queryFn: () => centralAPI.getOwnSupportTickets(filters),
        enabled: !!authenticatedUser
    });

    const { data, refetch } = ticketQuery;

    const isLoading = !!authenticatedUser && ticketQuery.isFetching;

    useEffect(() => {
        if (!data) {
            return;
        }
        setSupportTickets(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, navigation, filters]));

    return (
        <Container>
            <Header title={translate('myTicket')}
                actions={
                    <CustomButton
                        text={translate('createNewTicket')}
                        onPress={() => {
                            window.location.href = '/contact-us';
                        }}
                    />
                } />
            <Card>
                <DataTableHeader
                    title={translate('registeredSupportTickets')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('subject'),
                                selector: row => row.subject,
                                grow: 2,
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                selector: row => translate(row.status),
                            },
                            {
                                name: translate('priority'),
                                selector: row => translate(`${row.priority}Priority`),
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                name: translate('updatedAt'),
                                selector: row => moment(row.updatedAt).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                button: true,
                                cell: row => {
                                    return <DropdownButton
                                        actions={[{
                                            content: translate('viewInformation'),
                                            onPress: () => {
                                                navigation.navigate('GetOwnSupportTicket', { id: row.id });
                                            }
                                        }]}
                                    />;
                                }
                            }
                        ]}
                        data={supportTickets}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}
